import { useGetUrl } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { Loader, Stack, Text } from 'braid-design-system';
import React from 'react';

import { ExternalLink } from 'src/components/ExternalLink/ExternalLink';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { SALARY } from 'src/components/Router';
import { useAppConfig } from 'src/modules/appConfig';
import { NEXT_ROLE_SALARY_FORM } from 'src/reducers/forms';

import { LocationGroupSubtitle } from '../../ReadNextRole/LocationGroupSubtitle/LocationGroupSubtitle';
import { NextRoleDetail } from '../../ReadNextRole/NextRoleDetail/NextRoleDetail';
import { NextRoleSection } from '../../types';
import { EditSalary } from '../EditSalary/EditSalary';
import { useSalaryPreferences } from '../data';

import translations from './.vocab';

export const ReadSalary = () => {
  const { t } = useTranslations(translations);

  const { salaryPreferences, error } = useSalaryPreferences();
  const { brand, zone, language, isUnifiedSite } = useAppConfig();

  const hasMultipleSalaryPreferences = salaryPreferences.length >= 2;

  const getUrl = useGetUrl(zone, language, isUnifiedSite);

  const drawerDescription = (
    <>
      {t(
        'Your expected base salary will help you to be found by relevant employers.',
      )}
      &nbsp;
      {brand === 'SEEK' &&
        t('Learn more', {
          ExternalLink: (v) => (
            <ExternalLink
              target="_blank"
              rel="noopener noreferrer"
              weight="weak"
              hitArea="large"
              href={getUrl('first-impression-seek-profile')}
            >
              {v}
            </ExternalLink>
          ),
        })}
    </>
  );

  return (
    <>
      <NextRoleDetail
        title={t('Salary expectation')}
        section={NextRoleSection.salary}
        overrideAddButton={
          error
            ? false
            : salaryPreferences.filter(
                (salaryPreference) => salaryPreference.description !== null,
              ).length === 0
        }
      >
        {error ? (
          <Loader data={{ automation: 'salary-loading' }} />
        ) : (
          salaryPreferences.length > 0 && (
            <Stack space="medium" component="ul">
              {salaryPreferences.map((salaryItem) => {
                const hasDescription = salaryItem.description !== null;
                const tone = !hasDescription ? 'secondary' : 'neutral';

                return (
                  <Stack key={`salary-${salaryItem.id}`} space="small">
                    {hasMultipleSalaryPreferences && (
                      <LocationGroupSubtitle
                        countryCode={salaryItem.country.countryCode}
                      />
                    )}
                    <Text tone={tone}>
                      {salaryItem?.description || t('Not specified')}
                    </Text>
                  </Stack>
                );
              })}
            </Stack>
          )
        )}
      </NextRoleDetail>
      <FormDrawer
        title={t('Salary expectation')}
        component={EditSalary}
        card={SALARY}
        description={drawerDescription}
        formName={NEXT_ROLE_SALARY_FORM}
      />
    </>
  );
};
